<script lang="ts" setup>
import { computed, reactive, watch } from 'vue';
import { useVuelidateWithLogging } from '@/composables/useVuelidateWithLogging';
import { TrashIcon } from '@heroicons/vue/solid';

import { isValidPhone, required, maxLength, uniquePhone } from '@/utils/validators';
import { ContactPhoneFormState } from '@/types';

const props = defineProps({
    modelValue: {
        type: Object as () => ContactPhoneFormState,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
    canRemove: {
        type: Boolean,
        default: true,
    },
    phones: {
        type: Array as () => ContactPhoneFormState[],
        required: true,
    },
});

const emit = defineEmits(['update:modelValue', 'remove', 'set-main']);

const formState = reactive<ContactPhoneFormState>(props.modelValue);

const rules = computed(() => ({
    number: {
        isValidPhone,
        required,
        maxLength: maxLength(50),
        uniquePhone: uniquePhone(props.phones, props.index),
        _key: {},
    },
    isMain: { required },
}));

const { v$, $validateWithLogging } = useVuelidateWithLogging(rules, formState);

defineExpose({
    validate: () => $validateWithLogging(),
});

watch(
    formState,
    () => {
        emit('update:modelValue', formState);
    },
    {
        deep: true,
    },
);
</script>

<template>
    <div class="mt-2 flex items-center space-x-2 space-y-2 py-2">
        <UiTextInput
            v-model="v$.number.$model"
            name="number"
            type="text"
            :label="$t('contact.form.labels.phone')"
            class="flex-1"
            :errors="v$.number.$errors"
            @blur="v$.number.$touch"
        />
        <div class="grid grid-cols-2 gap-2 pt-4">
            <input
                type="radio"
                v-tippy="$t('contact.form.actions.setMainPhone')"
                :checked="formState.isMain"
                class="text-primary focus:ring-primary mt-0.5"
                @change="emit('set-main')"
            />
            <UiActionButton
                variant="danger"
                v-tippy="
                    canRemove
                        ? $t('contact.form.actions.removePhone')
                        : $t('contact.form.actions.cannotRemovePhone')
                "
                class=""
                :class="{ 'cursor-not-allowed opacity-40': !canRemove }"
                @click="emit('remove', index)"
            >
                <TrashIcon class="h-5 w-5" />
            </UiActionButton>
        </div>
    </div>
</template>
